/**
 * ErrorBoundary
 * ------------
 * React class component that catches render-time errors anywhere in its
 * descendant tree and renders a friendly fallback instead of a white page.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <BrowserRouter>...</BrowserRouter>
 *   </ErrorBoundary>
 *
 * Why: a render error in any descendant component (bad data, undefined
 * reference, missing prop) would otherwise unmount the entire tree and
 * leave the user staring at a blank screen. The boundary catches the
 * error, logs it, and shows a recoverable UI.
 *
 * Notes:
 *   - Error boundaries only catch render / lifecycle / constructor
 *     errors. They do NOT catch: event handlers (wrap those in try/catch),
 *     async code, SSR errors, or errors thrown inside the boundary itself.
 *   - We render the error message inside a <details> block so it's
 *     available for debugging but doesn't dominate the screen.
 *   - We offer a full-page reload as the recovery action — safest reset.
 */

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Called during the render phase. Return a state update so the
    // next render shows the fallback UI instead of the children.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Called during the commit phase. Good place for logging.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload() {
    // Full page reload — clears any stale state that might be causing
    // the render error. (window.location.reload() bypasses React state.)
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      const errMsg = (this.state.error && this.state.error.message)
        || 'Unknown render error';
      const stack = (this.state.error && this.state.error.stack)
        || '';
      const componentStack = (this.state.errorInfo && this.state.errorInfo.componentStack)
        || '';

      return (
        <div className="error-boundary">
          <div className="error-boundary-card">
            <h1 className="error-boundary-title">⚠️ Something went wrong</h1>
            <p className="error-boundary-lead">
              The page failed to render. Your data is safe — just reload to continue.
            </p>

            <button
              className="error-boundary-reload-btn"
              onClick={this.handleReload}
              type="button"
            >
              🔄 Reload page
            </button>

            <details className="error-boundary-details">
              <summary>Error details (for debugging)</summary>
              <pre className="error-boundary-stack">
                <strong>Message:</strong> {errMsg}{'\n\n'}
                {stack && (
                  <>
                    <strong>Stack:</strong>{'\n'}
                    {stack}{'\n\n'}
                  </>
                )}
                {componentStack && (
                  <>
                    <strong>Component stack:</strong>{'\n'}
                    {componentStack}
                  </>
                )}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
